import React, {ChangeEvent, ChangeEventHandler, FC, useEffect, useState} from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getAllBrochures, postBrochureFile } from '../../common/async/AsyncCalls';
import { IUserState } from '../../common/state/profile/user';
import SimpleSpinner from '../loaders/spinners/SimpleSpinner';
import { BsFolderX } from 'react-icons/bs';
import { FcPicture } from 'react-icons/fc';

type IFileUploadCardProps = {
    onSelect: (url: string) => void
};

const FileUploadCard : FC<IFileUploadCardProps> = ({onSelect}) => {
    const fileTypeList = ['image/jpeg', 'image/png', 'image/jpg'];
    // states
    const token: string = useSelector(({user} : {
        user: IUserState
    }) => user.token);    
    const [isLoading, toggleLoading] = useState(false);
    const [isSubmitDisabled, toggleSubmitDisabled] = useState(true);
    const [fileSelected, setFileSelected] = useState<File | undefined>(undefined);
    const [errorText, setErrorText] = useState("");
    const [brochureList, setBrochureList] = useState(<React.Fragment/>);

    function renderBrochureList(list: any[]) {
        if (list.length > 0) {
            setBrochureList(
                <ul className="max-h-5/6 overflow-y-scroll rounded-lg w-full text-gray-700 dark:text-gray-200 bg-gray-200 p-2 dark:bg-gray-800">
                    {list.map((item: any, index: number)=> <li key={index} 
                    onClick={()=>onSelect(item.url)}
                    className="cursor-pointer rounded px-4 py-2 w-full 
                    hover:border-2 hover:border-primary-300 hover:dark:border-teal-400 hover:bg-gray-300 
                    hover:dark:bg-gray-900 flex">
                        <FcPicture className="relative mt-1 mr-2 text-xl"/> <p>{item.name}</p>
                    </li>)}
                </ul>);
        } else {
            setBrochureList(
                <p className="overflow-y-scroll flex flex-wrap rounded-lg w-full text-gray-700 dark:text-gray-200 bg-gray-200 p-4 dark:bg-gray-800">
                    <BsFolderX className="text-xl relative mt-1 mr-4"/> There are no brochures
                </p>
            );
        }
    }

    // upload file
    function handleFileUpload() {
        toggleLoading(true);
        console.log("Uploading file details: ", fileSelected);
        if (fileSelected) uploadBrochure(fileSelected);
    }

    //evt handlers
    function handleFileChange (event: ChangeEvent<HTMLInputElement>) {
        setErrorText("");        
        setFileSelected(undefined);
        // console.log('File uploaded: ', event.currentTarget.files);
        if (!event.currentTarget.files || event.currentTarget.files.length == 0) return;
        const file: File = event.currentTarget.files[0];            
        if (!fileTypeList.includes(file.type)) {
            setErrorText("*Only Files of type - jpg/png/jpeg are supported");
            return;
        }
        setErrorText("");        
        setFileSelected(file);
    }
    
    const uploadBrochure = useCallback(async (file: File) => {
        try {
            toggleLoading(true);
            const formData = new FormData();
            formData.append('file', file);
            const result = await postBrochureFile(token, formData);
            // console.log('Response from server (file upload): ', result.data);            
            fetchBrochures();
        } catch (err) {
            setErrorText("*Error uploading file / file with similar name already exists!");
            console.log('Error uploading brochures', (err as Error).message);
        } finally {
            setFileSelected(undefined);
            toggleLoading(false);
        }
    },[token]);

    const fetchBrochures = useCallback(async ()=>{
        try {
            toggleLoading(true);
            const result = await getAllBrochures(token);
            // console.log('Brochure list: ', result.data);
            const list: any[] = result.data;
            renderBrochureList(list);
        } catch (err) {            
            console.log('Error fetching brochures', (err as Error).message);
        } finally {
            toggleLoading(false);
        }
    },[token]);

    useEffect(()=>{
        toggleSubmitDisabled(true);
        if (Boolean(fileSelected)) return toggleSubmitDisabled(false);        
    },[fileSelected]);

    // first time
    useEffect(()=>{
        fetchBrochures();
    },[]);

    return <React.Fragment>
        <section
        id="file-upload-section"
        className="my-3 mr-3 grow lg:w-1/2 flex flex-col justify-start rounded-xl p-4 shadow-lg bg-white dark:bg-gray-900">
        <p className="text-xs font-bold uppercase my-1">Borchure images</p>    
        <SimpleSpinner loading={isLoading}/>
        <div className="flex justify-start my-4">
            {brochureList}
        </div>

        <div className="w-full">
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-teal-400" htmlFor="file_input">Upload file</label>
            <input
                onChange={handleFileChange}
                className="block w-full p-2 my-2 text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400" id="file_input" type="file"/>
            <p id="file-upload-validation" className="visible text-red-500 text-sm font-medium my-2">{errorText}</p>
        </div>

        <button
            disabled={isSubmitDisabled} 
            onClick={handleFileUpload}
            className="bg-blue-500 hover:bg-blue-400 disabled:bg-blue-200 cursor-pointer disabled:cursor-not-allowed 
                    text-white font-bold py-2 px-4 border-b-4 border-blue-700 hover:border-blue-500 disabled:border-blue-200 rounded">
            Upload Brochure
        </button>
    </section>
    </React.Fragment>
};

export default FileUploadCard;